import React, {useEffect, useState} from 'react';
import {motion} from 'framer-motion';
import {legalVaultService} from '../services/legalVaultService';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {useNavigate} from 'react-router-dom';

const {FiLayers, FiArrowRight, FiPlus, FiCopy, FiEye, FiAlertCircle, FiX} = FiIcons;

const slugifyTemplateTitle = (value = '') =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

function AlertModal({message, onClose}) {
  if (!message) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center">
            <SafeIcon icon={FiAlertCircle} className="mr-2 text-blue-600" /> Alert
          </h3>
          <p className="text-slate-600 mt-2">{message}</p>
        </div>
        <div className="flex justify-end p-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">OK</button>
        </div>
      </div>
    </div>
  );
}

function PreviewTemplateModal({template, onClose}) {
  if (!template) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-3xl max-h-[88vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-slate-200 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{template.name}</h3>
            <p className="text-sm text-slate-500 mt-1">{template.type}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg" aria-label="Close preview">
            <SafeIcon icon={FiX} />
          </button>
        </div>
        <div className="p-6 overflow-y-auto">
          {template.description && <p className="text-sm text-slate-600 mb-5">{template.description}</p>}
          <pre className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-4 font-mono">
            {template.starterContent || 'No starter content has been added to this template.'}
          </pre>
        </div>
      </div>
    </div>
  );
}

function NewTemplateModal({isOpen, onClose, onSave, brands}) {
  const [formData, setFormData] = useState({name: '', type: '', description: '', starterContent: '', tags: '', brandId: ''});
  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.name || !formData.type) return;
    try {
      await onSave(formData);
      setFormData({name: '', type: '', description: '', starterContent: '', tags: '', brandId: ''});
      onClose();
    } catch {
      // Parent surfaces the backend error.
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center"><SafeIcon icon={FiPlus} className="mr-2 text-blue-600" /> Create New Template</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Template Title *</label>
              <input type="text" value={formData.name} onChange={(event) => setFormData({...formData, name: event.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" placeholder="e.g., Standard NDA" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Document Type *</label>
              <select value={formData.type} onChange={(event) => setFormData({...formData, type: event.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" required>
                <option value="">Select type...</option>
                <option value="Service Terms & Conditions">Service Terms & Conditions</option>
                <option value="Website Design Terms">Website Design Terms</option>
                <option value="Software Application Terms">Software Application Terms</option>
                <option value="SaaS Terms">SaaS Terms</option>
                <option value="Support & Maintenance Policies">Support & Maintenance Policies</option>
                <option value="Licence Terms">Licence Terms</option>
                <option value="Privacy Policies">Privacy Policies</option>
                <option value="Data Sharing Policies">Data Sharing Policies</option>
                <option value="Refund and Cancellation Policies">Refund and Cancellation Policies</option>
                <option value="Disclaimers">Disclaimers</option>
                <option value="Custom">Custom</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea value={formData.description} onChange={(event) => setFormData({...formData, description: event.target.value})} rows={2} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none" placeholder="Brief description of this template" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Starter Content</label>
            <textarea value={formData.starterContent} onChange={(event) => setFormData({...formData, starterContent: event.target.value})} rows={6} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none font-mono" placeholder="# Document Title&#10;&#10;Start writing your template content here using Markdown..." />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Tags</label>
              <input type="text" value={formData.tags} onChange={(event) => setFormData({...formData, tags: event.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" placeholder="legal, compliance, policy" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Assigned Brand</label>
              <select value={formData.brandId} onChange={(event) => setFormData({...formData, brandId: event.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                <option value="">Select brand...</option>
                {brands.map((brand) => <option key={brand.id} value={brand.id}>{brand.name}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors text-sm font-medium">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">Create Template</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [alertMessage, setAlertMessage] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [brands, setBrands] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([legalVaultService.getTemplates(), legalVaultService.getBrandingProfiles()]).then(([templatesData, brandsData]) => {
      setTemplates(templatesData);
      setBrands(brandsData);
    });
  }, []);

  const handleUseTemplate = async (templateId) => {
    try {
      const template = await legalVaultService.getTemplateById(templateId);
      const brand = brands.find((profile) => profile.isDefault) || brands.find((profile) => profile.isActive !== false) || brands[0];
      const baseSlug = slugifyTemplateTitle(template.name.replace(/template/gi, '')) || 'legal-document';
      const uniqueSlug = `${baseSlug}-${Date.now().toString().slice(-6)}`;
      const newDoc = await legalVaultService.createDocument({
        title: template.name.replace(' Template', ''),
        type: template.type,
        slug: uniqueSlug,
        tags: template.tags?.join(',') || '',
        brandId: brand?.id || '',
        visibility: 'Private',
        seoTitle: `${template.name} | ${brand?.name || 'BPA Solutions'}`,
        seoDescription: template.description,
        internalNotes: `Created from template: ${template.name}`,
        reviewDate: '',
      });
      const newVersion = await legalVaultService.createVersion(newDoc.id, {
        versionNumber: 'v1.0',
        status: 'Draft',
        content: template.starterContent,
        changeSummary: `Document created from template: ${template.name}`,
      });
      await legalVaultService.addAuditEntry({
        action: 'Document Created',
        documentId: newDoc.id,
        documentTitle: newDoc.title,
        version: newVersion.versionNumber,
        details: {source: 'template', templateId: template.id, templateName: template.name},
      });
      navigate(`/documents/${newDoc.id}/versions/${newVersion.id}/edit`);
    } catch (error) {
      setAlertMessage(`Error creating document from template: ${error.message}`);
    }
  };

  const handleDuplicateTemplate = async (templateId) => {
    try {
      await legalVaultService.duplicateTemplate(templateId);
      setTemplates(await legalVaultService.getTemplates());
    } catch (error) {
      setAlertMessage(`Error duplicating template: ${error.message}`);
    }
  };

  const handleCreateTemplate = async (templateData) => {
    try {
      await legalVaultService.createTemplate({...templateData, isActive: true});
      setTemplates(await legalVaultService.getTemplates());
    } catch (error) {
      setAlertMessage(`Error creating template: ${error.message}`);
      throw error;
    }
  };

  return (
    <motion.div initial={{opacity: 0}} animate={{opacity: 1}} className="space-y-6">
      <div className="flex justify-between items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Document Templates</h2>
          <p className="text-slate-500 mt-1">Start from a pre-approved template to maintain consistency.</p>
        </div>
        <button onClick={() => setShowNewModal(true)} className="flex items-center px-4 py-2 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors">
          <SafeIcon icon={FiPlus} className="mr-2" /> New Template
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <div key={template.id} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-all flex flex-col h-full">
            <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center mb-4"><SafeIcon icon={FiLayers} className="text-xl" /></div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">{template.name}</h3>
            <p className="text-sm text-slate-500 flex-1 mb-4">{template.description}</p>
            <div className="mt-auto pt-4 border-t border-slate-100">
              <p className="text-xs text-slate-400 mb-3">Type: {template.type}</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setPreviewTemplate(template)} className="inline-flex items-center px-3 py-1.5 bg-white border border-slate-300 text-slate-700 rounded-lg text-xs font-semibold hover:bg-slate-50 transition-colors">
                  <SafeIcon icon={FiEye} className="mr-1.5" /> Preview
                </button>
                <button onClick={() => handleDuplicateTemplate(template.id)} className="inline-flex items-center px-3 py-1.5 bg-purple-50 border border-purple-100 text-purple-700 rounded-lg text-xs font-semibold hover:bg-purple-100 transition-colors">
                  <SafeIcon icon={FiCopy} className="mr-1.5" /> Duplicate
                </button>
                <button onClick={() => handleUseTemplate(template.id)} className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors">
                  Use Template <SafeIcon icon={FiArrowRight} className="ml-1.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <AlertModal message={alertMessage} onClose={() => setAlertMessage(null)} />
      <PreviewTemplateModal template={previewTemplate} onClose={() => setPreviewTemplate(null)} />
      <NewTemplateModal isOpen={showNewModal} onClose={() => setShowNewModal(false)} onSave={handleCreateTemplate} brands={brands} />
    </motion.div>
  );
}